import type { ProductType } from '@econtractor/types';
import type { ProductMatchResult } from './types';

const normalizeName = (value: string): string =>
    String(value || '')
        .toLowerCase()
        .replace(/["'`]/g, ' ')
        .replace(/[^a-zа-яіїєґ0-9]+/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const tokenScore = (left: string, right: string): number => {
    if (!left || !right) return 0;
    if (left === right) return 1;
    if (left.includes(right) || right.includes(left)) return 0.95;

    const leftTokens = new Set(left.split(' ').filter(Boolean));
    const rightTokens = new Set(right.split(' ').filter(Boolean));
    if (!leftTokens.size || !rightTokens.size) return 0;

    let overlap = 0;
    leftTokens.forEach((token) => {
        if (rightTokens.has(token)) overlap += 1;
    });
    const maxSize = Math.max(leftTokens.size, rightTokens.size);
    return maxSize ? overlap / maxSize : 0;
};

export const matchProductByName = (nameRaw: string, products: ProductType[]): ProductMatchResult => {
    const sourceName = normalizeName(nameRaw);
    if (!sourceName) return { product: null, mode: 'new', score: 0 };

    const exact = products.find((item) => normalizeName(item?.prodName || '') === sourceName);
    if (exact) return { product: exact, mode: 'exact', score: 1 };

    let bestProduct: ProductType | null = null;
    let bestScore = 0;
    products.forEach((item) => {
        const score = tokenScore(sourceName, normalizeName(item?.prodName || ''));
        if (score > bestScore) {
            bestScore = score;
            bestProduct = item;
        }
    });

    if (bestProduct && bestScore >= 0.55) {
        return { product: bestProduct, mode: 'similar', score: bestScore };
    }

    return { product: null, mode: 'new', score: 0 };
};
